package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminClientAccountEntry
import org.gymly.model.AdminClientAccountEntryType
import org.gymly.model.AdminClientAccountApplyCredit
import org.gymly.model.AdminClientAccountStatement
import org.gymly.model.AdminClientAccountSummary
import org.gymly.model.AdminMembershipOrder
import org.gymly.model.AdminMembershipOrderStatus
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.util.GymlyCurrency
import org.gymly.util.formatMoney
import org.gymly.util.userMessage
import org.gymly.ui.components.VerticalScrollColumn

@Composable
fun AdminClientAccountsScreen() {
    val scope = rememberCoroutineScope()
    var summaries by remember { mutableStateOf<List<AdminClientAccountSummary>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var selectedClientId by remember { mutableStateOf<String?>(null) }
    var detailStatement by remember { mutableStateOf<AdminClientAccountStatement?>(null) }
    var detailLoading by remember { mutableStateOf(false) }
    var detailError by remember { mutableStateOf<String?>(null) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                summaries = AdminRepo.api.listClientAccounts()
            } catch (e: Exception) {
                val base = e.userMessage("No se pudo cargar el monedero digital")
                error = if (base.contains("Unexpected error", ignoreCase = true)) {
                    "$base. Si acabas de actualizar, reinicia el backend para aplicar la migración V61 (client_accounts)."
                } else {
                    base
                }
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(Unit) { reload() }

    val walletSummaries = remember(summaries) { summaries.filter { it.balanceCents > 0 } }
    val filtered = remember(walletSummaries, query) { walletSummaries.filterWalletCredits(query) }
    val listPaging = rememberAdminListPaging(
        initialSortKey = "balance",
        initialDir = AdminSortDir.Desc,
        resetKey = listOf(query),
    )
    val pageCount = adminPageCount(filtered.size)
    val safePage = listPaging.page.coerceIn(0, (pageCount - 1).coerceAtLeast(0))
    val pageItems = remember(filtered, listPaging.sort, safePage) {
        filtered.adminSortedPaged(listPaging.sort, safePage) { key ->
            when (key) {
                "balance" -> { s -> s.balanceCents }
                else -> { s -> s.clientName.lowercase() }
            }
        }
    }

    LaunchedEffect(query, filtered.size) {
        if (query.isNotBlank() && filtered.size == 1) {
            selectedClientId = filtered.first().clientId
        }
    }

    LaunchedEffect(selectedClientId) {
        val clientId = selectedClientId
        if (clientId == null) {
            detailStatement = null
            detailError = null
            return@LaunchedEffect
        }
        detailLoading = true
        detailError = null
        try {
            detailStatement = AdminRepo.api.clientAccountStatement(clientId)
        } catch (e: Exception) {
            detailStatement = null
            detailError = e.userMessage("No se pudo cargar el detalle del monedero")
        } finally {
            detailLoading = false
        }
    }
    val currency = summaries.firstOrNull()?.currency ?: GymlyCurrency.CODE

    BoxWithConstraints(Modifier.fillMaxSize().background(AdminColors.Surface)) {
        val compact = maxWidth < 700.dp
        Column(
            Modifier
                .fillMaxSize()
                .padding(if (compact) GymlySpace.md else 24.dp),
        ) {
            Text("Monedero digital", fontWeight = FontWeight.Bold, fontSize = if (compact) 20.sp else 22.sp, color = AdminColors.Text)
            Text(
                "Crédito disponible por pagos en exceso. Úsalo desde órdenes o ventas con «Usar saldo a favor».",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp, bottom = 16.dp),
            )

            when {
                loading -> BoxCentered { CircularProgressIndicator(color = AdminColors.Primary) }
                error != null -> {
                    Text(error!!, color = AdminColors.Error, fontSize = 13.sp)
                    Spacer(Modifier.height(12.dp))
                    Button(onClick = { reload() }, colors = ButtonDefaults.buttonColors(containerColor = AdminColors.Primary)) {
                        Text("Reintentar")
                    }
                }
                else -> {
                    ClientAccountsOverviewChart(
                        summaries = walletSummaries,
                        currency = currency,
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(14.dp))
                            .background(AdminColors.SurfaceContainer),
                    )

                    Spacer(Modifier.height(16.dp))

                    OutlinedTextField(
                        value = query,
                        onValueChange = { query = it },
                        modifier = Modifier.fillMaxWidth(),
                        placeholder = { Text("Buscar socio…") },
                        leadingIcon = { Icon(Icons.Outlined.Search, contentDescription = null) },
                        singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = AdminColors.Primary,
                            unfocusedBorderColor = AdminColors.Outline.copy(alpha = 0.5f),
                        ),
                    )

                    Spacer(Modifier.height(12.dp))

                    Text(
                        if (filtered.size == walletSummaries.size) {
                            "${walletSummaries.size} socios con saldo a favor"
                        } else {
                            "${filtered.size} de ${walletSummaries.size} socios con saldo"
                        },
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                        modifier = Modifier.padding(bottom = 8.dp),
                    )

                    if (walletSummaries.isEmpty()) {
                        Text("Ningún socio tiene saldo a favor en el monedero.", color = AdminColors.TextMuted)
                    } else if (filtered.isEmpty()) {
                        Text("Ningún socio coincide con el filtro.", color = AdminColors.TextMuted)
                    } else {
                        Column(
                            Modifier
                                .weight(1f)
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(12.dp))
                                .background(AdminColors.SurfaceContainer),
                        ) {
                            if (compact) {
                                VerticalScrollColumn(
                                    Modifier.weight(1f).fillMaxWidth().padding(12.dp),
                                    verticalArrangement = Arrangement.spacedBy(10.dp),
                                ) {
                                    pageItems.forEach { summary ->
                                        ClientAccountMobileCard(
                                            summary = summary,
                                            selected = summary.clientId == selectedClientId,
                                        ) {
                                            selectedClientId = summary.clientId
                                        }
                                    }
                                    selectedClientId?.let {
                                        ClientAccountInlineDetail(
                                            loading = detailLoading,
                                            error = detailError,
                                            statement = detailStatement,
                                            onChanged = { reload() },
                                        )
                                    }
                                }
                            } else {
                                Column(Modifier.weight(1f).fillMaxWidth()) {
                                    ClientAccountsListHeader(listPaging)
                                    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
                                    pageItems.forEachIndexed { index, summary ->
                                        ClientAccountListRow(
                                            summary = summary,
                                            selected = summary.clientId == selectedClientId,
                                        ) {
                                            selectedClientId = summary.clientId
                                        }
                                        if (index < pageItems.lastIndex) {
                                            HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.2f))
                                        }
                                    }
                                    if (pageItems.isEmpty()) {
                                        Text(
                                            "Sin socios en esta página.",
                                            Modifier.padding(16.dp),
                                            color = AdminColors.TextMuted,
                                            fontSize = 13.sp,
                                        )
                                    }
                                    selectedClientId?.let {
                                        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
                                        ClientAccountInlineDetail(
                                            loading = detailLoading,
                                            error = detailError,
                                            statement = detailStatement,
                                            onChanged = { reload() },
                                            modifier = Modifier.padding(16.dp),
                                        )
                                    }
                                }
                            }
                            AdminListPaginationBar(
                                totalItems = filtered.size,
                                page = safePage,
                                onPageChange = { listPaging.page = it },
                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 4.dp),
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ClientAccountInlineDetail(
    loading: Boolean,
    error: String?,
    statement: AdminClientAccountStatement?,
    onChanged: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val scope = rememberCoroutineScope()
    var applying by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var pendingOrders by remember { mutableStateOf<List<AdminMembershipOrder>>(emptyList()) }

    LaunchedEffect(statement?.clientId) {
        val clientId = statement?.clientId ?: return@LaunchedEffect
        pendingOrders = runCatching {
            AdminRepo.api.listMembershipOrders()
                .filter { it.clientId == clientId && it.status == AdminMembershipOrderStatus.PENDING }
        }.getOrElse { emptyList() }
    }

    Column(modifier, verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text("Detalle del saldo", fontWeight = FontWeight.Bold, fontSize = 15.sp, color = AdminColors.Text)
        when {
            loading -> BoxCentered { CircularProgressIndicator(color = AdminColors.Primary) }
            error != null -> Text(error, color = AdminColors.Error, fontSize = 13.sp)
            statement != null -> {
                Text(
                    "${statement.clientName} · ${formatMoney(statement.balanceCents.toInt(), statement.currency)}",
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 14.sp,
                    color = AdminColors.Primary,
                )
                ClientAccountMovementsTable(
                    entries = statement.entries.walletMovementsOnly(),
                    currency = statement.currency,
                )
                if (statement.balanceCents > 0 && pendingOrders.isNotEmpty()) {
                    Text("Aplicar saldo a orden pendiente", fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
                    pendingOrders.forEach { order ->
                        Row(
                            Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Column(Modifier.weight(1f)) {
                                Text("${order.displayNumber()} · ${order.planName}", fontSize = 13.sp)
                                Text(
                                    formatMoney(order.amountCents.toInt(), order.currency),
                                    fontSize = 12.sp,
                                    color = AdminColors.TextMuted,
                                )
                            }
                            Button(
                                onClick = {
                                    scope.launch {
                                        applying = true
                                        message = null
                                        try {
                                            AdminRepo.api.applyClientAccountCredit(
                                                statement.clientId,
                                                AdminClientAccountApplyCredit(membershipOrderId = order.id),
                                            )
                                            message = "Saldo aplicado a ${order.displayNumber()}."
                                            onChanged()
                                        } catch (e: Exception) {
                                            message = e.userMessage("No se pudo aplicar el saldo")
                                        } finally {
                                            applying = false
                                        }
                                    }
                                },
                                enabled = !applying,
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = AdminColors.Primary,
                                    contentColor = AdminColors.OnBrand,
                                ),
                            ) {
                                Text(if (applying) "…" else "Aplicar")
                            }
                        }
                    }
                }
                message?.let { Text(it, color = AdminColors.Primary, fontSize = 13.sp) }
            }
        }
    }
}

@Composable
private fun ClientAccountMovementsTable(
    entries: List<AdminClientAccountEntry>,
    currency: String,
) {
    if (entries.isEmpty()) {
        Text("Sin movimientos en el monedero.", color = AdminColors.TextMuted, fontSize = 13.sp)
        return
    }
    Row(
        Modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        AdminListHeaderLabel("Fecha", Modifier.weight(1.2f))
        AdminListHeaderLabel("Concepto", Modifier.weight(1.4f))
        AdminListHeaderLabel("Monto", Modifier.weight(0.9f))
        AdminListHeaderLabel("Saldo", Modifier.weight(0.9f))
    }
    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
    entries.forEach { entry ->
        ClientAccountMovementRow(entry, currency)
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.15f))
    }
}

@Composable
private fun ClientAccountMovementRow(entry: AdminClientAccountEntry, currency: String) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            entry.effectiveAt.take(10),
            Modifier.weight(1.2f),
            fontSize = 13.sp,
            color = AdminColors.Text,
        )
        Text(
            entry.entryType.labelEs(),
            Modifier.weight(1.4f),
            fontSize = 13.sp,
            color = AdminColors.Text,
        )
        Text(
            formatMoney(entry.amountCents.toInt(), currency),
            Modifier.weight(0.9f),
            fontSize = 13.sp,
            fontWeight = FontWeight.Medium,
            color = when (entry.entryType) {
                AdminClientAccountEntryType.CREDIT_FROM_OVERPAY,
                AdminClientAccountEntryType.ADJUSTMENT,
                -> AdminColors.Primary
                AdminClientAccountEntryType.CREDIT_APPLY -> AdminColors.Error
                else -> AdminColors.Text
            },
        )
        Text(
            formatMoney(entry.balanceAfterCents.toInt(), currency),
            Modifier.weight(0.9f),
            fontSize = 13.sp,
            fontWeight = FontWeight.SemiBold,
            color = AdminColors.Primary,
        )
    }
}

@Composable
private fun ClientAccountMobileCard(
    summary: AdminClientAccountSummary,
    selected: Boolean = false,
    onClick: () -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(
                if (selected) AdminColors.Primary.copy(alpha = 0.1f) else AdminColors.Surface,
            )
            .clickable(onClick = onClick)
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Text(summary.clientName, fontWeight = FontWeight.SemiBold, fontSize = 15.sp, color = AdminColors.Text)
        summary.clientEmail?.let {
            Text(it, color = AdminColors.TextMuted, fontSize = 12.sp, maxLines = 1)
        }
        Column {
            Text("Saldo a favor", color = AdminColors.TextMuted, fontSize = 12.sp)
            Text(
                formatMoney(summary.balanceCents.toInt(), summary.currency),
                fontWeight = FontWeight.SemiBold,
                fontSize = 15.sp,
                color = AdminColors.Primary,
            )
        }
        Text("Ver detalle →", color = AdminColors.Primary, fontSize = 12.sp, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun ClientAccountsListHeader(listPaging: AdminListPaging) {
    Row(
        Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        AdminSortableHeader("Socio", "name", listPaging.sort, listPaging::onSort, Modifier.weight(1.6f))
        AdminSortableHeader("Saldo a favor", "balance", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
        Spacer(Modifier.size(40.dp))
    }
}

@Composable
private fun ClientAccountListRow(
    summary: AdminClientAccountSummary,
    selected: Boolean = false,
    onClick: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .background(if (selected) AdminColors.Primary.copy(alpha = 0.08f) else AdminColors.SurfaceContainer)
            .clickable(onClick = onClick)
            .padding(horizontal = 16.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(Modifier.weight(1.6f)) {
            Text(summary.clientName, fontWeight = FontWeight.Medium, fontSize = 14.sp, color = AdminColors.Text)
            summary.clientEmail?.let {
                Text(it, color = AdminColors.TextMuted, fontSize = 12.sp, maxLines = 1)
            }
        }
        Text(
            formatMoney(summary.balanceCents.toInt(), summary.currency),
            Modifier.weight(1f),
            fontSize = 14.sp,
            fontWeight = FontWeight.SemiBold,
            color = AdminColors.Primary,
        )
        IconButton(onClick = onClick, modifier = Modifier.size(40.dp)) {
            Icon(
                Icons.Outlined.Search,
                contentDescription = "Ver detalle del monedero",
                tint = AdminColors.Primary,
            )
        }
    }
}

@Composable
fun ClientAccountDetailDialog(
    clientId: String,
    onDismiss: () -> Unit,
    onChanged: () -> Unit = {},
) {
    val scope = rememberCoroutineScope()
    var statement by remember { mutableStateOf<AdminClientAccountStatement?>(null) }
    var pendingOrders by remember { mutableStateOf<List<AdminMembershipOrder>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var applying by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var message by remember { mutableStateOf<String?>(null) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                statement = AdminRepo.api.clientAccountStatement(clientId)
                pendingOrders = AdminRepo.api.listMembershipOrders()
                    .filter { it.clientId == clientId && it.status == AdminMembershipOrderStatus.PENDING }
            } catch (e: Exception) {
                error = e.userMessage("No se pudo cargar el detalle")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(clientId) { reload() }

    Dialog(onDismissRequest = onDismiss) {
        VerticalScrollColumn(
            Modifier
                .widthIn(max = 760.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text(
                    statement?.clientName ?: "Monedero digital",
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                )
                Icon(
                    Icons.Outlined.Close,
                    contentDescription = "Cerrar",
                    tint = AdminColors.TextMuted,
                    modifier = Modifier.clickable(onClick = onDismiss),
                )
            }

            Spacer(Modifier.height(12.dp))

            when {
                loading -> BoxCentered { CircularProgressIndicator(color = AdminColors.Primary) }
                error != null -> Text(error!!, color = AdminColors.Error)
                statement != null -> {
                    ClientAccountStatementBody(
                        statement = statement!!,
                        pendingOrders = pendingOrders,
                        applying = applying,
                        message = message,
                        onApplyCredit = { order ->
                            scope.launch {
                                applying = true
                                message = null
                                error = null
                                try {
                                    val result = AdminRepo.api.applyClientAccountCredit(
                                        clientId,
                                        AdminClientAccountApplyCredit(membershipOrderId = order.id),
                                    )
                                    message = "Se aplicaron ${formatMoney(result.appliedCents.toInt(), statement!!.currency)} de saldo."
                                    reload()
                                    onChanged()
                                } catch (e: Exception) {
                                    error = e.userMessage("No se pudo aplicar el saldo")
                                } finally {
                                    applying = false
                                }
                            }
                        },
                    )
                    Spacer(Modifier.height(16.dp))
                    ClientBalanceHistoryChart(
                        entries = statement!!.entries,
                        currency = statement!!.currency,
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(12.dp))
                            .background(AdminColors.Outline.copy(alpha = 0.08f)),
                    )
                }
            }

            Spacer(Modifier.height(16.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                Button(
                    onClick = onDismiss,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("Cerrar")
                }
            }
        }
    }
}

@Composable
fun ClientAccountStatementBody(
    statement: AdminClientAccountStatement,
    pendingOrders: List<AdminMembershipOrder> = emptyList(),
    applying: Boolean = false,
    message: String? = null,
    onApplyCredit: ((AdminMembershipOrder) -> Unit)? = null,
    compact: Boolean = false,
) {
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        SummaryChip(
            label = "Saldo a favor",
            value = formatMoney(statement.balanceCents.toInt(), statement.currency),
            highlight = statement.balanceCents > 0,
            modifier = Modifier.fillMaxWidth(),
        )

        message?.let {
            Text(it, color = AdminColors.Primary, fontSize = 13.sp)
        }

        if (statement.balanceCents > 0 && pendingOrders.isNotEmpty() && onApplyCredit != null) {
            Column(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(10.dp))
                    .background(AdminColors.Outline.copy(alpha = 0.12f))
                    .padding(12.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Text("Aplicar saldo a orden pendiente", fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
                pendingOrders.forEach { order ->
                    Row(
                        Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Column(Modifier.weight(1f)) {
                            Text("${order.displayNumber()} · ${order.planName}", fontSize = 13.sp, fontWeight = FontWeight.Medium)
                            Text(
                                formatMoney(order.amountCents.toInt(), order.currency),
                                fontSize = 12.sp,
                                color = AdminColors.TextMuted,
                            )
                        }
                        Button(
                            onClick = { onApplyCredit(order) },
                            enabled = !applying,
                            colors = ButtonDefaults.buttonColors(
                                containerColor = AdminColors.Primary,
                                contentColor = AdminColors.OnBrand,
                            ),
                        ) {
                            Text(if (applying) "…" else "Aplicar")
                        }
                    }
                }
            }
        }

        Text("Movimientos del monedero", fontWeight = FontWeight.SemiBold, fontSize = if (compact) 13.sp else 14.sp)

        val walletEntries = statement.entries.walletMovementsOnly()
        if (walletEntries.isEmpty()) {
            Text("Sin abonos ni usos de saldo registrados.", color = AdminColors.TextMuted, fontSize = 13.sp)
        } else {
            walletEntries.forEach { entry ->
                ClientAccountEntryRow(entry, statement.currency, compact)
            }
        }
    }
}

@Composable
private fun ClientAccountSummaryCard(summary: AdminClientAccountSummary, onClick: () -> Unit) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.SurfaceContainer)
            .clickable(onClick = onClick)
            .padding(16.dp),
    ) {
        Text(summary.clientName, fontWeight = FontWeight.SemiBold, fontSize = 15.sp)
        summary.clientEmail?.let {
            Text(it, color = AdminColors.TextMuted, fontSize = 12.sp)
        }
        Spacer(Modifier.height(8.dp))
        Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
            Text(
                "Saldo ${formatMoney(summary.balanceCents.toInt(), summary.currency)}",
                fontSize = 13.sp,
                color = AdminColors.Primary,
            )
        }
    }
}

@Composable
private fun SummaryChip(
    label: String,
    value: String,
    highlight: Boolean,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier
            .clip(RoundedCornerShape(10.dp))
            .background(if (highlight) AdminColors.Primary.copy(alpha = 0.08f) else AdminColors.Outline.copy(alpha = 0.12f))
            .padding(12.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(
            value,
            fontWeight = FontWeight.Bold,
            fontSize = 16.sp,
            color = if (highlight) AdminColors.Primary else AdminColors.Text,
        )
    }
}

@Composable
private fun ClientAccountEntryRow(
    entry: org.gymly.model.AdminClientAccountEntry,
    currency: String,
    compact: Boolean,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(AdminColors.Outline.copy(alpha = 0.08f))
            .padding(horizontal = 12.dp, vertical = if (compact) 8.dp else 10.dp),
    ) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Text(entry.entryType.labelEs(), fontWeight = FontWeight.Medium, fontSize = 13.sp)
            Text(
                formatMoney(entry.amountCents.toInt(), currency),
                fontWeight = FontWeight.SemiBold,
                fontSize = 13.sp,
                color = when (entry.entryType) {
                    org.gymly.model.AdminClientAccountEntryType.PAYMENT,
                    org.gymly.model.AdminClientAccountEntryType.CREDIT_FROM_OVERPAY,
                    -> AdminColors.Primary
                    org.gymly.model.AdminClientAccountEntryType.CREDIT_APPLY -> AdminColors.Error
                    else -> AdminColors.Text
                },
            )
        }
        entry.description?.let {
            Text(it, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(top = 2.dp))
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Text(entry.effectiveAt.take(19).replace('T', ' '), fontSize = 12.sp, color = AdminColors.TextMuted)
            if (entry.balanceAfterCents != 0L || entry.entryType == org.gymly.model.AdminClientAccountEntryType.CREDIT_APPLY || entry.entryType == org.gymly.model.AdminClientAccountEntryType.CREDIT_FROM_OVERPAY) {
                Text(
                    "Saldo ${formatMoney(entry.balanceAfterCents.toInt(), currency)}",
                    fontSize = 12.sp,
                    color = AdminColors.TextMuted,
                )
            }
        }
    }
}

@Composable
private fun BoxCentered(content: @Composable () -> Unit) {
    Row(
        Modifier.fillMaxWidth().padding(24.dp),
        horizontalArrangement = Arrangement.Center,
    ) {
        content()
    }
}
